<template>
  <div class="progress-bar__wrapper" :style="`--fillColor: ${shouldShowError ? '#e60000' : '#2a70ea'}`">
    <div class="progress-bar__labels">
      <div class="progress-bar__label">{{ valueLabel }}</div>
      <div v-if="shouldShowError" class="progress-bar__label progress-bar__label--error">
        {{ errorLabel }}
      </div>
      <div class="progress-bar__label">{{ maxLabel }}</div>
    </div>

    <div class="progress-bar__container">
      <div class="progress-bar__num-wrapper">
        <div class="progress-bar__num-circle progress-bar__num-circle--filled"></div>
        <div class="progress-bar__num">{{ modelValue.toLocaleString() }}</div>
      </div>

      <div class="progress-bar__bar-wrapper">
        <div class="progress-bar__bar-background"></div>
        <div class="progress-bar__bar-progress" :style="`--progressPercent: ${getProgressPercent()}%`"></div>
      </div>

      <div class="progress-bar__num-wrapper">
        <div class="progress-bar__num-circle"></div>
        <div class="progress-bar__num">{{ max.toLocaleString() }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'ProgressBar',

  props: {
    min: {
      type: Number,
      validator: (value) => value >= 0,
      default: 0,
    },

    max: {
      type: Number,
      required: true,
    },

    modelValue: {
      type: Number,
      required: true,
    },

    valueLabel: {
      type: String,
      required: true,
    },

    maxLabel: {
      type: String,
      required: true,
    },

    showError: {
      type: Boolean,
      default: false,
    },

    errorLabel: {
      type: String,
      default: null,
    },
  },

  computed: {
    maxedOut() {
      return this.modelValue >= this.max;
    },

    shouldShowError() {
      return this.maxedOut && this.showError;
    },
  },

  methods: {
    getProgressPercent() {
      let value: number = this.modelValue;

      if (this.maxedOut) {
        value = this.max;
      } else if (value < this.min) {
        value = this.min;
      }

      const percentage = ((value - this.min) / (this.max - this.min)) * 100;

      // if percentage is NaN, it means that max and min are equal, so it should return 0%
      return 100 - (isNaN(percentage) ? 100 : percentage);
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';
.progress-bar__wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 360px;
}

.progress-bar__labels {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.progress-bar__label {
  @include typography.typography-14-normal;
  color: colors.$color-grey-500;

  &--error {
    @include typography.typography-14-medium;
    color: var(--fillColor);
  }
}

.progress-bar__container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  width: 100%;
}

.progress-bar__bar-wrapper {
  position: relative;
  width: 217px;
  height: 8px;
  border-radius: settings.$border-radius * 2;
}

.progress-bar__bar-background {
  position: absolute;
  height: 8px;
  left: 0px;
  right: 0px;
  top: 0px;
  border-radius: settings.$border-radius;
  background: colors.$color-grey-300;
}

.progress-bar__bar-progress {
  position: absolute;
  height: 8px;
  left: 0%;
  right: var(--progressPercent);
  top: 0px;
  border-radius: settings.$border-radius;
  background: var(--fillColor);
}

.progress-bar__num-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}

.progress-bar__num-circle {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background-color: colors.$color-grey-300;

  &--filled {
    background-color: var(--fillColor);
  }
}

.progress-bar__num {
  @include typography.typography-14-normal;
}
</style>
